import { required, minValue, number, regex } from "react-admin";

export const TrainingVideoResources = "training-videos";

export const TAG_KEY_OPTIONS = [
  { value: "ring_material", label: "Ring Material", apiUrl: "custom-pricing/ring-materials", includeNoneOption: false },
  { value: "profile", label: "Profile", apiUrl: "custom-pricing/profiles", includeNoneOption: false },
  { value: "weight", label: "Weight", apiUrl: "custom-pricing/weights", includeNoneOption: false },
  { value: "ring_finish", label: "Ring Finish", apiUrl: "custom-pricing/finishes", includeNoneOption: false },
  { value: "second_ring_finish", label: "Second Ring Finish", apiUrl: "custom-pricing/finishes", includeNoneOption: false },
  { value: "sleeve", label: "Sleeve", apiUrl: "custom-pricing/sleeves", includeNoneOption: true },
  { value: "groove_group", label: "Groove Group", apiUrl: "custom-pricing/groove-groups", includeNoneOption: false },
  { value: "inlay_material", label: "Inlay Material", apiUrl: "custom-pricing/inlays", includeNoneOption: true },
  { value: "inlay_finish", label: "Inlay Finish", apiUrl: "custom-pricing/finishes", includeNoneOption: false },
  { value: "inlay_accent", label: "Inlay Accent", apiUrl: "custom-pricing/mga-color-options", includeNoneOption: false },
  { value: "gemstone_arrangement", label: "Gemstone Arrangement", apiUrl: "custom-pricing/gemstone-arrangements", includeNoneOption: false },
  { value: "gemstone_setting", label: "Gemstone Setting", apiUrl: "custom-pricing/gemstone-settings", includeNoneOption: false },
  { value: "gemstone_cut", label: "Gemstone Cut", apiUrl: "custom-pricing/gemstone-cuts", includeNoneOption: false },
  { value: "gemstone_size", label: "Gemstone Size", apiUrl: "custom-pricing/gemstone-dimensions", includeNoneOption: false },
  { value: "gemstone_material", label: "Gemstone Material", apiUrl: "custom-pricing/gemstones", includeNoneOption: true },
  { value: "machine_pattern", label: "Machine Pattern", apiUrl: "custom-pricing/machine-patterns", includeNoneOption: true },
  {
    value: "machine_pattern_cerakote_color",
    label: "Machine Pattern Cerakote Color",
    apiUrl: "custom-pricing/machine-pattern-color-options",
    includeNoneOption: false,
  },
];

export const getTagResource = key =>
  TAG_KEY_OPTIONS.find(option => option.value === key) || null;

export const createEmptyTagRow = () => ({ key: "", value: "" });

const isTagRow = tag => tag && typeof tag === "object" && "key" in tag && "value" in tag;

export const apiTagsToRows = tags => {
  if (!Array.isArray(tags)) return [];

  return tags.map(tag => {
    if (isTagRow(tag)) {
      return { key: tag.key || "", value: tag.value || "" };
    }

    if (tag && typeof tag === "object") {
      const keys = Object.keys(tag).filter(key => key !== "category" && key !== "name");
      if (keys.length === 1) {
        const key = keys[0];
        return { key, value: tag[key] || "" };
      }

      if (tag.name) {
        return { key: tag.category || "", value: tag.name };
      }
    }

    return createEmptyTagRow();
  });
};

export const rowsToApiTags = rows =>
  (Array.isArray(rows) ? rows : [])
    .map(row => {
      if (isTagRow(row) && row.key && row.value) {
        return { [row.key]: row.value };
      }

      if (row && typeof row === "object") {
        const keys = Object.keys(row).filter(key => key !== "category" && key !== "name");
        if (keys.length === 1 && row[keys[0]]) {
          return { [keys[0]]: row[keys[0]] };
        }
      }

      return null;
    })
    .filter(Boolean);

export const getTagLabel = tag => {
  if (!tag || typeof tag !== "object") return String(tag);

  if (isTagRow(tag)) {
    return tag.key ? `${tag.key}: ${tag.value}` : tag.value;
  }

  if (tag.name && !Object.keys(tag).some(key => TAG_KEY_OPTIONS.some(opt => opt.value === key))) {
    return tag.name;
  }

  const keys = Object.keys(tag).filter(key => key !== "category" && key !== "name");
  if (keys.length === 1) {
    const key = keys[0];
    const option = TAG_KEY_OPTIONS.find(opt => opt.value === key);
    const keyLabel = option ? option.label : key;
    return `${keyLabel}: ${tag[key]}`;
  }

  return JSON.stringify(tag);
};

export const validateTitle = [required(), regex(/.{1,255}/, "Title is required")];
export const validatePriority = [
  required(),
  number("Priority must be a number"),
  minValue(0, "Priority must be 0 or greater"),
];
export const validateVideoPath = [
  required("Please upload a video"),
  regex(/^videos\//, "Video path must start with videos/"),
];
export const validateVietnameseVideoPath = value => {
  if (!value) return undefined;
  return /^videos\//.test(value) ? undefined : "Video path must start with videos/";
};
export const validateIntroTime = [
  required("Intro time is required"),
  number("Intro time must be a number"),
  minValue(0, "Intro time must be 0 or greater"),
];
export const validateVietnameseIntroTime = (value, allValues) => {
  if (!allValues?.vietnamese_video_file_path) {
    return undefined;
  }

  if (value === undefined || value === null || value === "") {
    return "Vietnamese intro time is required when a Vietnamese video is provided";
  }

  const num = Number(value);
  if (Number.isNaN(num)) {
    return "Vietnamese intro time must be a number";
  }
  if (num < 0) {
    return "Vietnamese intro time must be 0 or greater";
  }

  return undefined;
};
export const validateTags = value => {
  const rows = apiTagsToRows(value);

  const hasIncomplete = rows.some(
    row =>
      row &&
      (row.key || row.value) &&
      ((row.key && !row.value) || (!row.key && row.value))
  );
  if (hasIncomplete) {
    return "Each tag must have both key and value";
  }

  return undefined;
};
